import type { MaybeRef, MaybeRefOrGetter, Ref } from "vue"
import type { ServerResponse } from "@/http"
import type { IAlbumMedia, IMediaResponse } from "@/types"
import { keepPreviousData, useQuery } from "@tanstack/vue-query"
import { computed, ref, toValue } from "vue"
import { axios } from "@/axios"
import { Media } from "@/models"

// eslint-disable-next-line unused-imports/no-unused-vars
const possibleIncludes = ["album", "tags", "reactions"] as const
export type IListMediaPossibleIncludes = (typeof possibleIncludes)[number]

export interface IListMediaQuery {
  page?: number
  perPage?: number
  sortBy?: string
  orderBy?: "asc" | "desc"
  type?: string // Possibly could be more narrow
  showGuestUploadsOnly?: boolean
  showPinnedUploadsOnly?: boolean
  tags?: string[]
  include?: IListMediaPossibleIncludes[]
  awaitingModeration?: boolean
}

interface IListMediaMeta {
  TotalPhotos?: number
  TotalVideos?: number
  TotalPhotosProcessing?: number
  TotalVideosProcessing?: number
  TotalAwaitingApproval?: number
}

export async function listMedia(eventVaultId?: number, albumId?: number, query?: IListMediaQuery): Promise<IAlbumMedia> {
  if (!eventVaultId) {
    throw new Error("Missing `eventVaultId` when attempting to query media. Please ensure `eventVaultId` is provided.")
  }

  const params = new URLSearchParams()

  if (query?.page)
    params.append("page", query.page.toString())
  if (query?.perPage)
    params.append("perPage", query.perPage.toString())
  if (query?.sortBy)
    params.append("sortBy", query.sortBy)
  if (query?.orderBy && query.orderBy !== "desc")
    params.append("orderByDesc", "false")
  if (query?.type)
    params.append("type", query.type)
  if (query?.showGuestUploadsOnly)
    params.append("showGuestUploadsOnly", query.showGuestUploadsOnly.toString())
  if (query?.showPinnedUploadsOnly)
    params.append("showPinnedUploadsOnly", query.showPinnedUploadsOnly.toString())
  if (query?.tags?.length)
    params.append("tags", query.tags.toString())
  if (query?.include)
    params.append("include", query.include.toString())
  if (query?.awaitingModeration)
    params.append("awaitingModeration", query.awaitingModeration.toString())

  const endpoint = albumId ? `api/guest/${eventVaultId}/album/${albumId}/media` : `api/guest/${eventVaultId}/media`

  const response = await axios.get<ServerResponse<IMediaResponse[], IListMediaMeta>>(`${endpoint}?${params.toString()}`)

  const media: Media[] = response.data.Data?.map(media => Media.fromResponse(media)) ?? []

  const meta = {
    pendingMediaCount:
            (response.data.Meta?.TotalPhotosProcessing ?? 0) + (response.data.Meta?.TotalVideosProcessing ?? 0),
    totalPhotos: response.data.Meta?.TotalPhotos ?? 0,
    totalVideos: response.data.Meta?.TotalVideos ?? 0,
    totalAwaitingApproval: response.data.Meta?.TotalAwaitingApproval ?? 0,
  }

  return {
    media,
    links: response.data.Links,
    meta,
  }
}

export const LIST_MEDIA_QUERY_KEY = "listMedia"

export interface UseListMediaOptions {
  pageCountRef?: Ref<number>
  totalMediaRef?: Ref<number>
  pendingApprovalCountRef?: Ref<number>
}

export function useListMedia(
  eventVaultId: MaybeRef<number | undefined>,
  albumId: MaybeRef<number | undefined> = undefined,
  queryParams: MaybeRefOrGetter<IListMediaQuery> = {},
  options: UseListMediaOptions = {},
) {
  const { pageCountRef, totalMediaRef, pendingApprovalCountRef } = options

  const pageCount = pageCountRef ?? ref(1)
  const totalMedia = totalMediaRef ?? ref(0)
  const pendingApprovalCount = pendingApprovalCountRef ?? ref(0)

  const query = useQuery({
    queryKey: [LIST_MEDIA_QUERY_KEY, eventVaultId, albumId, queryParams],
    queryFn: async () => {
      const evId = toValue(eventVaultId)
      const albId = toValue(albumId)
      const params = toValue(queryParams)

      const response = await listMedia(evId, albId, params)

      if (response.links?.Last)
        pageCount.value = Number(response.links.Last)

      if (response.meta?.totalPhotos || response.meta?.totalVideos)
        totalMedia.value = (response.meta.totalVideos ?? 0) + (response.meta.totalPhotos ?? 0)

      if (response.meta?.totalAwaitingApproval != null)
        pendingApprovalCount.value = response.meta.totalAwaitingApproval

      return response
    },
    enabled: computed(() => !!toValue(eventVaultId)),
    placeholderData: keepPreviousData,
  })

  return {
    ...query,
    pageCount,
    totalMedia,
    pendingApprovalCount,
  }
}
